/**
 * Adobe Analytics event name constants.
 *
 * Naming convention:
 *   - All event names use the neutral `om_*` prefix — no `_pa_` or `_cl_` in the string.
 *   - User identity is sent as `user_type` ('PA' | 'CL') derived from the runtime role,
 *     not embedded in the tag name.
 *   - Use `OM_EVENTS` for all new code.
 *   - `PATIENT_EVENTS`, `CLINICIAN_EVENTS`, and `SHARED_EVENTS` are backward-compat aliases.
 */

export const OM_EVENTS = {
  PAGE: {
    HOMEPAGE: 'om_homepage',
    LOGIN: 'om_login',
    PRIVACY: 'om_privacy',
    PRIVACY_POLICY: 'om_privacy_policy',
    ABOUT_US: 'om_about_us',
    CONTACT_US: 'om_contact_us',
    FAQ: 'om_faq',
    TERMS_OF_USE: 'om_terms_of_use',
    SETTINGS: 'om_settings',
    EXPECTATION: 'om_expectation',
    DISCLAIMER: 'om_disclaimer',
    ACCESS_WALL: 'om_access_wall',
    MINOR: 'om_minor',
    BEGIN_SEARCH: 'om_begin_search',
    SELECT_SPECIALTY: 'om_select_specialty',
    MY_MATCHES: 'om_my_matches',
    MY_SURVEY: 'om_my_survey',
    SURVEY_REVIEW: 'om_survey_review',
    SURVEY_FEEDBACK: 'om_survey_feedback',
    POST_SURVEY_FEEDBACK: 'om_post_survey_feedback',
    REVIEW_RESPONSES: 'om_review_responses',
    MY_SURVEY_RESPONSES: 'om_my_survey_responses',
    MY_SURVEY_RESPONSES_MODAL_EXIT_CONF:
      'om_my_survey_responses_modal_exit_conf',
    CLINICIAN_PROFILE: 'om_clinician_profile',
    CLINICIAN_PROFILE_SCHEDULE_MODAL: 'om_clinician_profile_schedule_modal',
    ZIP_MODAL: 'om_zip_modal',
    OUT_OF_AREA_MODAL: 'om_out_of_area_modal',
    ACCESS_WALL_MODAL: 'om_access_wall_modal',
    ALL_FILTERS_PANEL: 'om_all_filters_panel',
    PC_SURVEY_MODAL: 'om_pc_survey_modal',
    SIGNIN_SUCCESS: 'om_signin_success',
    DASHBOARD: 'om_dashboard',
    MY_PROFILE: 'om_my_profile',
    MANAGE_RESPONSES: 'om_manage_responses',
    PC_SURVEY: 'om_pc_survey',
    CLINICAL_ASSISTANCE: 'om_clinical_assistance',
    MSID_SIGNIN_SUCCESS: 'om_msid_signin_success',
    OHID_SIGNIN_SUCCESS: 'om_ohid_signin_success',
    MSID_SIGNIN_CLICK: 'om_msid_signin_click',
    OHID_SIGNIN_CLICK: 'om_ohid_signin_click',
  },

  BUTTON_CLICK: {
    SAVE_CLINICIAN: 'om_button_click:save_clinician',
    UNSAVE_CLINICIAN: 'om_button_click:unsave_clinician',
    WAYS_WE_MATCH: 'om_button_click:ways_we_match',
    CLOSE_YOUR_MATCH_DETAILS: 'om_button_click:close_your_match_details',
    VIEW_PROFILE: 'om_button_click:view_profile',
    OPEN_FILTERS: 'om_button_click:open_filters',
    CLEAR_FILTERS: 'om_button_click:clear_filters',
    CLOSE_FILTERS: 'om_button_click:close_filters',
    FILTERS_APPLY: 'om_button_click:filters_apply',
    PAGINATION_NEXT: 'om_button_click:pagination_next',
    PAGINATION_PREV: 'om_button_click:pagination_prev',
    DONE: 'om_button_click:done',
    EDIT_RESPONSE: 'om_button_click:edit_response',
    BACK_REVIEW_RESPONSE: 'om_button_click:back_review_response',
    SAVE_RESPONSE: 'om_button_click:save_response',
    CLOSE_QUESTION: 'om_button_click:close_question',
    BACK: 'om_button_click:back',
    CONTINUE_QUESTION: 'om_button_click:continue_{question}',
    BACK_QUESTION: 'om_button_click:back_{question}',
    NEXT_QUESTION: 'om_button_click:next_{question}',
    PRACTICE_STYLE: 'om_button_click:practice_style',
    CLINICAL_SERVICES_I_OFFER: 'om_button_click:clinical_services_i_offer',
    IM_SUPPORTIVE_OF: 'om_button_click:im_supportive_of',
    MY_CARE_APPROACHES: 'om_button_click:my_care_approaches',
    CONTINUE_EDITING: 'om_button_click:continue_editing',
    LEAVE_WITHOUT_SAVING: 'om_button_click:leave_without_saving',
    MANAGE_MY_RESPONSES: 'om_button_click:manage_my_responses',
  },

  LINK_CLICK: {
    FOOTER_HOME: 'om_link_click:footer_home',
    FOOTER_ABOUT_US: 'om_link_click:footer_about_us',
    FOOTER_CONTACT_US: 'om_link_click:footer_contact_us',
    FOOTER_FAQ: 'om_link_click:footer_faq',
    FOOTER_PRIVACY_POLICY: 'om_link_click:footer_privacy_policy',
    FOOTER_TERMS_OF_USE: 'om_link_click:footer_terms_of_use',
    SIGN_IN: 'om_link_click:sign_in',
    SIGN_IN_CLINICIAN: 'om_link_click:sign_in_clinician',
    SIGN_IN_PATIENT: 'om_link_click:sign_in_patient',
    SEE_MORE: 'om_link_click:see_more',
    BASED_ON_SURVEY_PRIMARY_CARE: 'om_link_click:based_on_survey_primary_care',
    LOCATION_MAP: 'om_link_click:location_map',
    CONTACT_US: 'om_link_click:contact_us',
    MENU_SETTINGS: 'om_link_click:menu_settings',
    MENU_ABOUT_US: 'om_link_click:menu_about_us',
    MENU_PRIVACY_POLICY: 'om_link_click:menu_privacy_policy',
    MENU_TERMS_OF_USE: 'om_link_click:menu_terms_of_use',
    MENU_FAQ: 'om_link_click:menu_faq',
    MENU_CONTACT_US: 'om_link_click:menu_contact_us',
    MENU_CLINICAL_ASSISTANCE: 'om_link_click:menu_clinical_assistance',
    VIEW_CONTACT_US: 'om_link_click:view_contact_us',
    VIEW_FAQ: 'om_link_click:view_faq',
  },

  CTA_CLICK: {
    GET_STARTED: 'om_cta_click:get_started',
    BEGIN_SURVEY: 'om_cta_click:begin_survey',
    EASY: 'om_cta_click:easy',
    DIFFICULT: 'om_cta_click:difficult',
    SAVE_CHANGES: 'om_cta_click:save_changes',
    CONTINUE_SURVEY_COMMENCEMENT: 'om_cta_click:continue_survey_commencement',
    CARD_PROFILE_IMAGE: 'om_cta_click:card_profile_image_click',
    VIEW_PROFILE: 'om_cta_click:view_profile',
    VIEW_SCHEDULING_OPTIONS: 'om_cta_click:view_scheduling_options',
    SUBMIT_SURVEY: 'om_cta_click:submit_survey',
    TRY_DIFFERENT_ZIP: 'om_cta_click:try_different_zip',
    VIEW_UPDATED_MATCHES: 'om_cta_click:view_updated_matches',
    CREATE_ACCOUNT: 'om_cta_click:create_account_access_wall',
    SIGN: 'om_cta_click:sign',
    MY_PROFILE: 'om_cta_click:my_profile',
    LAUNCH_SURVEY: 'om_cta_click:launch_survey',
    PROFILE_PIC: 'om_cta_click:profile_pic',
  },

  NAV_CLICK: {
    LOGO: 'om_nav_click:logo',
    ABOUT_US: 'om_nav_click:about_us',
    CONTACT_US: 'om_nav_click:contact_us',
    FAQ: 'om_nav_click:faq',
    SETTINGS: 'om_nav_click:settings',
    HOME: 'om_nav_click:home',
  },

  CLICK: {
    LOGO: 'om_click:logo',
    OUR_COMPATIBILITY: 'om_click:our_compatibility',
    ABOUT_ME: 'om_click:about_me',
    MY_CARE: 'om_click:my_care',
    LOCATIONS: 'om_click:locations',
    DASHBOARD: 'om_click:dashboard',
    MY_PROFILE: 'om_click:my_profile',
    MANAGE_RESPONSES: 'om_click:manage_responses',
    MANAGE_RESPONSE: 'om_click:manage_response',
  },

  DROPDOWN: {
    SORT_BY: 'om_dropdown_open:sort_by',
    HOURS_OF_OPERATION: 'om_dropdown_click:hours_of_operation',
    COMPATIBILITY_EXCELLENT: 'om_dropdown_change:compatibility_excellent',
    COMPATIBILITY_GOOD: 'om_dropdown_change:compatibility_good',
    COMPATIBILITY_FAIR: 'om_dropdown_change:compatibility_fair',
  },

  MODAL: {
    DISMISS_SCHEDULING: 'om_modal_dismiss:scheduling',
    DISMISS_UNABLE_TO_SAVE: 'om_modal_dismiss:unable_to_save_changes',
    DISMISS_SURVEY_UPDATE_SUCCESS_X: 'om_modal_dismiss_x:survey_update_success',
    DISMISS_SURVEY_UPDATE_SUCCESS: 'om_modal_dismiss:survey_update_success',
    DISMISS_LEAVE_SURVEY_EDITING: 'om_modal_dismiss:leave_survey_editing',
  },

  ACCORDION: {
    ADD_AN_INTRODUCTION: 'om_accordion_toggle:add_an_introduction',
    LOCATION_MAPS_LINK: 'om_accordion_toggle:location_maps_link',
    LOCATION_DETAILS: 'om_accordion_toggle:location_details',
    PRACTICE_STYLE: 'om_accordion_toggle:practice_style',
    CLINICAL_SERVICES_I_OFFER: 'om_accordion_toggle:clinical_services_i_offer',
    IM_SUPPORTIVE_OF: 'om_accordion_toggle:im_supportive_of',
    MY_CARE_APPROACHES: 'om_accordion_toggle:my_care_approaches',
  },

  CHECKBOX: {
    AGREED_TO_TEXT: 'om_check_box_toggle:agreed_to_receive_text_messages',
  },

  ZIP: {
    NOT_AVAILABLE: 'om_zip_code_not_available',
  },

  PAGE_VIEW: {
    DASHBOARD: 'om_page_view:dashboard',
  },
} as const

export const INTENT_EVENTS = {
  SURVEY_SELECTED: 'survey_selected',
  SURVEY_STARTED: 'survey_started',
  SURVEY_EDIT_STARTED: 'survey_edit_started',
  FILTER_UPDATED: 'filter_updated',
} as const

// Backward-compat aliases — existing import sites continue to compile.
// New code should use OM_EVENTS directly.

export const SHARED_EVENTS = {
  PAGE: {
    SELECT_SPECIALTY: OM_EVENTS.PAGE.SELECT_SPECIALTY,
    HOMEPAGE: OM_EVENTS.PAGE.HOMEPAGE,
    ABOUT_US: OM_EVENTS.PAGE.ABOUT_US,
    CONTACT_US: OM_EVENTS.PAGE.CONTACT_US,
    FAQ: OM_EVENTS.PAGE.FAQ,
    PRIVACY_POLICY: OM_EVENTS.PAGE.PRIVACY_POLICY,
    TERMS_OF_USE: OM_EVENTS.PAGE.TERMS_OF_USE,
    SETTINGS: OM_EVENTS.PAGE.SETTINGS,
  },
  LINK_CLICK: OM_EVENTS.LINK_CLICK,
  NAV_CLICK: OM_EVENTS.NAV_CLICK,
  CTA_CLICK: {
    GET_STARTED: OM_EVENTS.CTA_CLICK.GET_STARTED,
    BEGIN_SURVEY: OM_EVENTS.CTA_CLICK.BEGIN_SURVEY,
    EASY: OM_EVENTS.CTA_CLICK.EASY,
    DIFFICULT: OM_EVENTS.CTA_CLICK.DIFFICULT,
    SAVE_CHANGES: OM_EVENTS.CTA_CLICK.SAVE_CHANGES,
    CONTINUE_SURVEY_COMMENCEMENT:
      OM_EVENTS.CTA_CLICK.CONTINUE_SURVEY_COMMENCEMENT,
  },
} as const

export const PATIENT_EVENTS = {
  PAGE: {
    HOMEPAGE: OM_EVENTS.PAGE.HOMEPAGE,
    LOGIN: OM_EVENTS.PAGE.LOGIN,
    PRIVACY: OM_EVENTS.PAGE.PRIVACY,
    PRIVACY_POLICY: OM_EVENTS.PAGE.PRIVACY_POLICY,
    ABOUT_US: OM_EVENTS.PAGE.ABOUT_US,
    CONTACT_US: OM_EVENTS.PAGE.CONTACT_US,
    FAQ: OM_EVENTS.PAGE.FAQ,
    TERMS_OF_USE: OM_EVENTS.PAGE.TERMS_OF_USE,
    EXPECTATION: OM_EVENTS.PAGE.EXPECTATION,
    DISCLAIMER: OM_EVENTS.PAGE.DISCLAIMER,
    ACCESS_WALL: OM_EVENTS.PAGE.ACCESS_WALL,
    MINOR: OM_EVENTS.PAGE.MINOR,
    BEGIN_SEARCH: OM_EVENTS.PAGE.BEGIN_SEARCH,
    MY_MATCHES: OM_EVENTS.PAGE.MY_MATCHES,
    MY_SURVEY: OM_EVENTS.PAGE.MY_SURVEY,
    SURVEY_REVIEW: OM_EVENTS.PAGE.SURVEY_REVIEW,
    SURVEY_FEEDBACK: OM_EVENTS.PAGE.SURVEY_FEEDBACK,
    POST_SURVEY_FEEDBACK: OM_EVENTS.PAGE.POST_SURVEY_FEEDBACK,
    REVIEW_RESPONSES: OM_EVENTS.PAGE.REVIEW_RESPONSES,
    MY_SURVEY_RESPONSES: OM_EVENTS.PAGE.MY_SURVEY_RESPONSES,
    MY_SURVEY_RESPONSES_APPROVED: OM_EVENTS.PAGE.MY_SURVEY_RESPONSES,
    MY_SURVEY_RESPONSES_MODAL_EXIT_CONF:
      OM_EVENTS.PAGE.MY_SURVEY_RESPONSES_MODAL_EXIT_CONF,
    CLINICIAN_PROFILE: OM_EVENTS.PAGE.CLINICIAN_PROFILE,
    CLINICIAN_PROFILE_SCHEDULE_MODAL:
      OM_EVENTS.PAGE.CLINICIAN_PROFILE_SCHEDULE_MODAL,
    ZIP_MODAL: OM_EVENTS.PAGE.ZIP_MODAL,
    OUT_OF_AREA_MODAL: OM_EVENTS.PAGE.OUT_OF_AREA_MODAL,
    ACCESS_WALL_MODAL: OM_EVENTS.PAGE.ACCESS_WALL_MODAL,
    ALL_FILTERS_PANEL: OM_EVENTS.PAGE.ALL_FILTERS_PANEL,
    PC_SURVEY_MODAL: OM_EVENTS.PAGE.PC_SURVEY_MODAL,
    SIGNIN_SUCCESS: OM_EVENTS.PAGE.SIGNIN_SUCCESS,
  },
  BUTTON_CLICK: {
    SAVE_CLINICIAN: OM_EVENTS.BUTTON_CLICK.SAVE_CLINICIAN,
    UNSAVE_CLINICIAN: OM_EVENTS.BUTTON_CLICK.UNSAVE_CLINICIAN,
    WAYS_WE_MATCH: OM_EVENTS.BUTTON_CLICK.WAYS_WE_MATCH,
    CLOSE_YOUR_MATCH_DETAILS: OM_EVENTS.BUTTON_CLICK.CLOSE_YOUR_MATCH_DETAILS,
    VIEW_PROFILE: OM_EVENTS.BUTTON_CLICK.VIEW_PROFILE,
    OPEN_FILTERS: OM_EVENTS.BUTTON_CLICK.OPEN_FILTERS,
    CLEAR_FILTERS: OM_EVENTS.BUTTON_CLICK.CLEAR_FILTERS,
    CLOSE_FILTERS: OM_EVENTS.BUTTON_CLICK.CLOSE_FILTERS,
    FILTERS_APPLY: OM_EVENTS.BUTTON_CLICK.FILTERS_APPLY,
    PAGINATION_NEXT: OM_EVENTS.BUTTON_CLICK.PAGINATION_NEXT,
    PAGINATION_PREV: OM_EVENTS.BUTTON_CLICK.PAGINATION_PREV,
    DONE: OM_EVENTS.BUTTON_CLICK.DONE,
    EDIT_RESPONSE: OM_EVENTS.BUTTON_CLICK.EDIT_RESPONSE,
    BACK_REVIEW_RESPONSE: OM_EVENTS.BUTTON_CLICK.BACK_REVIEW_RESPONSE,
    SAVE_RESPONSE: OM_EVENTS.BUTTON_CLICK.SAVE_RESPONSE,
    CLOSE_QUESTION: OM_EVENTS.BUTTON_CLICK.CLOSE_QUESTION,
    BACK: OM_EVENTS.BUTTON_CLICK.BACK,
    CONTINUE_QUESTION: OM_EVENTS.BUTTON_CLICK.CONTINUE_QUESTION,
    BACK_QUESTION: OM_EVENTS.BUTTON_CLICK.BACK_QUESTION,
    PRACTICE_STYLE: OM_EVENTS.BUTTON_CLICK.PRACTICE_STYLE,
    CLINICAL_SERVICES_I_OFFER: OM_EVENTS.BUTTON_CLICK.CLINICAL_SERVICES_I_OFFER,
    IM_SUPPORTIVE_OF: OM_EVENTS.BUTTON_CLICK.IM_SUPPORTIVE_OF,
    MY_CARE_APPROACHES: OM_EVENTS.BUTTON_CLICK.MY_CARE_APPROACHES,
    CONTINUE_EDITING: OM_EVENTS.BUTTON_CLICK.CONTINUE_EDITING,
    LEAVE_WITHOUT_SAVING: OM_EVENTS.BUTTON_CLICK.LEAVE_WITHOUT_SAVING,
  },
  LINK_CLICK: {
    FOOTER_HOME: OM_EVENTS.LINK_CLICK.FOOTER_HOME,
    FOOTER_ABOUT_US: OM_EVENTS.LINK_CLICK.FOOTER_ABOUT_US,
    FOOTER_CONTACT_US: OM_EVENTS.LINK_CLICK.FOOTER_CONTACT_US,
    FOOTER_FAQ: OM_EVENTS.LINK_CLICK.FOOTER_FAQ,
    FOOTER_PRIVACY_POLICY: OM_EVENTS.LINK_CLICK.FOOTER_PRIVACY_POLICY,
    FOOTER_TERMS_OF_USE: OM_EVENTS.LINK_CLICK.FOOTER_TERMS_OF_USE,
    SEE_MORE: OM_EVENTS.LINK_CLICK.SEE_MORE,
    BASED_ON_SURVEY_PRIMARY_CARE:
      OM_EVENTS.LINK_CLICK.BASED_ON_SURVEY_PRIMARY_CARE,
    LOCATION_MAP: OM_EVENTS.LINK_CLICK.LOCATION_MAP,
    CONTACT_US: OM_EVENTS.LINK_CLICK.CONTACT_US,
  },
  CTA_CLICK: {
    CARD_PROFILE_IMAGE: OM_EVENTS.CTA_CLICK.CARD_PROFILE_IMAGE,
    VIEW_PROFILE: OM_EVENTS.CTA_CLICK.VIEW_PROFILE,
    VIEW_SCHEDULING_OPTIONS: OM_EVENTS.CTA_CLICK.VIEW_SCHEDULING_OPTIONS,
    SUBMIT_SURVEY: OM_EVENTS.CTA_CLICK.SUBMIT_SURVEY,
    TRY_DIFFERENT_ZIP: OM_EVENTS.CTA_CLICK.TRY_DIFFERENT_ZIP,
    SAVE_CHANGES: OM_EVENTS.CTA_CLICK.SAVE_CHANGES,
    VIEW_UPDATED_MATCHES: OM_EVENTS.CTA_CLICK.VIEW_UPDATED_MATCHES,
    EASY: OM_EVENTS.CTA_CLICK.EASY,
    DIFFICULT: OM_EVENTS.CTA_CLICK.DIFFICULT,
    PAGE_VIEW_MY_MATCHES: 'om_page_view:my_matches',
    CREATE_ACCOUNT: OM_EVENTS.CTA_CLICK.CREATE_ACCOUNT,
    SIGN: OM_EVENTS.CTA_CLICK.SIGN,
  },
  CLICK: {
    LOGO: OM_EVENTS.CLICK.LOGO,
    OUR_COMPATIBILITY: OM_EVENTS.CLICK.OUR_COMPATIBILITY,
    ABOUT_ME: OM_EVENTS.CLICK.ABOUT_ME,
    MY_CARE: OM_EVENTS.CLICK.MY_CARE,
    LOCATIONS: OM_EVENTS.CLICK.LOCATIONS,
  },
  DROPDOWN: OM_EVENTS.DROPDOWN,
  MODAL: OM_EVENTS.MODAL,
  CHECKBOX: OM_EVENTS.CHECKBOX,
  ZIP: OM_EVENTS.ZIP,
} as const

export const CLINICIAN_EVENTS = {
  PAGE: {
    HOME: OM_EVENTS.PAGE.HOMEPAGE,
    DASHBOARD: OM_EVENTS.PAGE.DASHBOARD,
    LOGIN: OM_EVENTS.PAGE.LOGIN,
    PRIVACY: OM_EVENTS.PAGE.PRIVACY,
    PRIVACY_POLICY: OM_EVENTS.PAGE.PRIVACY_POLICY,
    ABOUT_US: OM_EVENTS.PAGE.ABOUT_US,
    CONTACT_US: OM_EVENTS.PAGE.CONTACT_US,
    FAQ: OM_EVENTS.PAGE.FAQ,
    TERMS_OF_USE: OM_EVENTS.PAGE.TERMS_OF_USE,
    SETTINGS: OM_EVENTS.PAGE.SETTINGS,
    EXPECTATION: OM_EVENTS.PAGE.EXPECTATION,
    CLINICAL_ASSISTANCE: OM_EVENTS.PAGE.CLINICAL_ASSISTANCE,
    EXPECTATIONS: OM_EVENTS.PAGE.EXPECTATION,
    MY_PROFILE: OM_EVENTS.PAGE.MY_PROFILE,
    MANAGE_RESPONSES: OM_EVENTS.PAGE.MANAGE_RESPONSES,
    SURVEY_REVIEW: OM_EVENTS.PAGE.SURVEY_REVIEW,
    SURVEY_FEEDBACK: OM_EVENTS.PAGE.SURVEY_FEEDBACK,
    REVIEW_RESPONSES: OM_EVENTS.PAGE.REVIEW_RESPONSES,
    MY_SURVEY_RESPONSES: OM_EVENTS.PAGE.MY_SURVEY_RESPONSES,
    MY_SURVEY_RESPONSES_MODAL_EXIT_CONF:
      OM_EVENTS.PAGE.MY_SURVEY_RESPONSES_MODAL_EXIT_CONF,
    PC_SURVEY: OM_EVENTS.PAGE.PC_SURVEY,
    POST_SURVEY_FEEDBACK: OM_EVENTS.PAGE.POST_SURVEY_FEEDBACK,
    MSID_SIGNIN_SUCCESS: OM_EVENTS.PAGE.MSID_SIGNIN_SUCCESS,
    OHID_SIGNIN_SUCCESS: OM_EVENTS.PAGE.OHID_SIGNIN_SUCCESS,
    SIGNIN_SUCCESS: OM_EVENTS.PAGE.SIGNIN_SUCCESS,
    MSID_SIGNIN_CLICK: OM_EVENTS.PAGE.MSID_SIGNIN_CLICK,
    OHID_SIGNIN_CLICK: OM_EVENTS.PAGE.OHID_SIGNIN_CLICK,
  },
  BUTTON_CLICK: {
    BACK_REVIEW_RESPONSE: OM_EVENTS.BUTTON_CLICK.BACK_REVIEW_RESPONSE,
    EDIT_RESPONSE: OM_EVENTS.BUTTON_CLICK.EDIT_RESPONSE,
    SAVE_RESPONSE: OM_EVENTS.BUTTON_CLICK.SAVE_RESPONSE,
    CLOSE_QUESTION: OM_EVENTS.BUTTON_CLICK.CLOSE_QUESTION,
    BACK: OM_EVENTS.BUTTON_CLICK.BACK,
    NEXT_QUESTION: OM_EVENTS.BUTTON_CLICK.NEXT_QUESTION,
    BACK_QUESTION: OM_EVENTS.BUTTON_CLICK.BACK_QUESTION,
    CONTINUE_EDITING: OM_EVENTS.BUTTON_CLICK.CONTINUE_EDITING,
    LEAVE_WITHOUT_SAVING: OM_EVENTS.BUTTON_CLICK.LEAVE_WITHOUT_SAVING,
    MANAGE_MY_RESPONSES: OM_EVENTS.BUTTON_CLICK.MANAGE_MY_RESPONSES,
  },
  PAGE_VIEW: OM_EVENTS.PAGE_VIEW,
  LINK_CLICK: {
    FOOTER_HOME: OM_EVENTS.LINK_CLICK.FOOTER_HOME,
    FOOTER_ABOUT_US: OM_EVENTS.LINK_CLICK.FOOTER_ABOUT_US,
    FOOTER_CONTACT_US: OM_EVENTS.LINK_CLICK.FOOTER_CONTACT_US,
    FOOTER_FAQ: OM_EVENTS.LINK_CLICK.FOOTER_FAQ,
    FOOTER_PRIVACY_POLICY: OM_EVENTS.LINK_CLICK.FOOTER_PRIVACY_POLICY,
    FOOTER_TERMS_OF_USE: OM_EVENTS.LINK_CLICK.FOOTER_TERMS_OF_USE,
    MENU_SETTINGS: OM_EVENTS.LINK_CLICK.MENU_SETTINGS,
    MENU_ABOUT_US: OM_EVENTS.LINK_CLICK.MENU_ABOUT_US,
    MENU_PRIVACY_POLICY: OM_EVENTS.LINK_CLICK.MENU_PRIVACY_POLICY,
    MENU_TERMS_OF_USE: OM_EVENTS.LINK_CLICK.MENU_TERMS_OF_USE,
    MENU_FAQ: OM_EVENTS.LINK_CLICK.MENU_FAQ,
    MENU_CONTACT_US: OM_EVENTS.LINK_CLICK.MENU_CONTACT_US,
    MENU_CLINICAL_ASSISTANCE: OM_EVENTS.LINK_CLICK.MENU_CLINICAL_ASSISTANCE,
    VIEW_CONTACT_US: OM_EVENTS.LINK_CLICK.VIEW_CONTACT_US,
    VIEW_FAQ: OM_EVENTS.LINK_CLICK.VIEW_FAQ,
  },
  CTA_CLICK: {
    SUBMIT_SURVEY: OM_EVENTS.CTA_CLICK.SUBMIT_SURVEY,
    MY_PROFILE: OM_EVENTS.CTA_CLICK.MY_PROFILE,
    EASY: OM_EVENTS.CTA_CLICK.EASY,
    DIFFICULT: OM_EVENTS.CTA_CLICK.DIFFICULT,
    LAUNCH_SURVEY: OM_EVENTS.CTA_CLICK.LAUNCH_SURVEY,
    PROFILE_PIC: OM_EVENTS.CTA_CLICK.PROFILE_PIC,
    SAVE_CHANGES: OM_EVENTS.CTA_CLICK.SAVE_CHANGES,
  },
  MODAL: OM_EVENTS.MODAL,
  NAV_CLICK: {
    CL_HOME: OM_EVENTS.NAV_CLICK.HOME,
    CL_ABOUT_US: OM_EVENTS.NAV_CLICK.ABOUT_US,
    CL_CONTACT_US: OM_EVENTS.NAV_CLICK.CONTACT_US,
    CL_FAQ: OM_EVENTS.NAV_CLICK.FAQ,
    CL_SETTINGS: OM_EVENTS.NAV_CLICK.SETTINGS,
  },
  CLICK: {
    DASHBOARD: OM_EVENTS.CLICK.DASHBOARD,
    MY_PROFILE: OM_EVENTS.CLICK.MY_PROFILE,
    MANAGE_RESPONSES: OM_EVENTS.CLICK.MANAGE_RESPONSES,
    MANAGE_RESPONSE: OM_EVENTS.CLICK.MANAGE_RESPONSE,
  },
  DROPDOWN: {
    HOURS_OF_OPERATION: OM_EVENTS.DROPDOWN.HOURS_OF_OPERATION,
  },
  ACCORDION: OM_EVENTS.ACCORDION,
} as const

export const formatClinicianProfileEventName = (
  firstName: string,
  lastName: string,
  id?: string
): string => {
  const fname = (firstName || '').replace(/\s+/g, '_')
  const lname = (lastName || '').replace(/\s+/g, '_')
  const suffix = id ? `_${id}` : ''
  return `om_clinician_profile_${fname}_${lname}${suffix}`
}

export const EVENTS = {
  PATIENT: PATIENT_EVENTS,
  CLINICIAN: CLINICIAN_EVENTS,
} as const
